import { useEffect, useRef, useState } from "react";
import {
  CALL_LENGTHS, CALL_TOPIC_MAX, callDraftFault, callWhen, canJoin, dashboardCalls, defaultCallDraft, draftFromCall,
  type Call, type CallDraft,
} from "../lib/calls";
import type { CallsState, CallWrite } from "../routes/useCalls";
import { JOIN_CALL, type JoinCallDetail } from "./CallScreen";

/* SCHEDULED CALLS ON THE WEB (his idea 2026-10-10, `docs/CALLS-BRIEF.md`, session 1): the Dashboard's `Calls` card, a client's
 * `Calls` line, and the ONE call window (book · move · cancel). Rules: `lib/calls.ts`; rows and writes: `useCalls`.
 *
 * ⚠️ `Join` IS WITHHELD OUTSIDE ITS WINDOW (10 min before → the end), never greyed. Every `Join` goes through `joinCall`,
 * the ONE door (session 2: it opens the call screen, `CallScreen.tsx`'s `CallHost`, on a stand-in room); the whole surface is
 * behind `CALLS_INCLUDED` (dev only) until session 3. */

/** THE ONE DOOR INTO A CALL — every `Join` calls this; the page's `CallHost` opens the screen. */
export function joinCall(c: Call, name: string): void {
  window.dispatchEvent(new CustomEvent<JoinCallDetail>(JOIN_CALL, { detail: { call: c, name } }));
}

export type CallTarget =
  | { mode: "book"; clientId: string; clientName: string; day: string | null }
  | { mode: "manage"; call: Call; clientName: string };

/** One call as a line: the client's name (`Call` on the client's own page), `30 min · topic` under it, the when, `Join` in its window. */
function CallLine({ c, name, joinName, now, zone, onOpen }: {
  readonly c: Call; readonly name: string | null; readonly joinName: string; readonly now: Date; readonly zone: string;
  readonly onOpen: () => void;
}) {
  const when = callWhen(c.startsAt, now, zone);
  return (
    <li className="call-row">
      <button type="button" className="call-body" onClick={onOpen}
              aria-label={`Call${name === null ? "" : ` with ${name}`}, ${when} — move or cancel`}>
        <span className="call-title">{name ?? "Call"}</span>
        <span className="call-detail">{[`${c.minutes} min`, c.topic].filter((p) => p !== null).join(" · ")}</span>
      </button>
      <span className={when.startsWith("today") ? "call-when is-today" : "call-when"}>{when}</span>
      {canJoin(c, now)
        ? <button type="button" className="ht-button is-go call-join" onClick={() => joinCall(c, joinName)}>Join</button>
        : <span className="call-join-room" aria-hidden="true" />}
    </li>
  );
}

/** THE DASHBOARD'S `Calls` CARD — the next seven days' calls, one line each; nothing at all when there is none. */
export function CallsCard({ state, coachId, now, zone, onOpen }: {
  readonly state: CallsState; readonly coachId: string; readonly now: Date; readonly zone: string;
  readonly onOpen: (t: CallTarget) => void;
}) {
  if (state.status !== "ready") return null;
  const rows = dashboardCalls(state.calls, coachId, now);
  if (rows.length === 0) return null;
  return (
    <section className="queue-card calls-card" aria-labelledby="calls-title">
      <header className="queue-card-head">
        <h2 id="calls-title" className="queue-card-title">Calls</h2>
      </header>
      <ul className="call-rows">
        {rows.map((c) => {
          const name = state.names[c.clientId] ?? "This client";
          return <CallLine key={c.id} c={c} name={name} joinName={name} now={now} zone={zone}
                           onOpen={() => onOpen({ mode: "manage", call: c, clientName: name })} />;
        })}
      </ul>
    </section>
  );
}

/** A CLIENT'S PAGE: this client's booked calls, under the header — drawn only when there is one. */
export function ClientCalls({ state, clientId, clientName, now, zone, onOpen }: {
  readonly state: CallsState; readonly clientId: string; readonly clientName: string; readonly now: Date; readonly zone: string;
  readonly onOpen: (t: CallTarget) => void;
}) {
  if (state.status !== "ready") return null;
  const rows = state.calls.filter((c) => c.clientId === clientId && c.cancelledAt === null && Date.parse(c.startsAt) + c.minutes * 60_000 > now.getTime());
  if (rows.length === 0) return null;
  return (
    <ul className="call-rows client-calls" aria-label={`Calls with ${clientName}`}>
      {rows.map((c) => <CallLine key={c.id} c={c} name={null} joinName={clientName} now={now} zone={zone}
                                 onOpen={() => onOpen({ mode: "manage", call: c, clientName })} />)}
    </ul>
  );
}

/** THE ONE CALL WINDOW — book (from a client's page) or a booked call (move · change · cancel). The house window: a native
 *  `<dialog>` over a dimmed page, Escape closes without saving, the ONE primary is Book/Save; cancel asks once, in place. */
export function CallWindow({ target, onClose, write, zone, now }: {
  readonly target: CallTarget | null;
  readonly onClose: () => void;
  readonly write: CallWrite;
  readonly zone: string;
  readonly now: Date;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const dayRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<CallDraft>({ day: "", time: "", minutes: 30, topic: "" });
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const open = target !== null;

  useEffect(() => {
    const el = dialogRef.current;
    if (el === null) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
    if (target === null) return;
    setDraft(target.mode === "book" ? defaultCallDraft(target.day, now, zone) : draftFromCall(target.call, zone));
    setRefusal(null); setBusy(false); setConfirmCancel(false);
  }, [open, target, now, zone]);
  useEffect(() => { if (open) dayRef.current?.focus(); }, [open]);

  async function save() {
    if (target === null) return;
    const fault = callDraftFault(draft, new Date(), zone);
    if (fault !== null) { setRefusal(fault); return; }
    setBusy(true); setRefusal(null);
    const failure = target.mode === "book" ? await write.book(target.clientId, draft) : await write.move(target.call, draft);
    setBusy(false);
    if (failure !== null) { setRefusal(failure); return; }
    onClose();
  }

  async function cancelCall() {
    if (target?.mode !== "manage") return;
    setBusy(true); setRefusal(null);
    const failure = await write.cancel(target.call);
    setBusy(false);
    if (failure !== null) { setRefusal(failure); return; }
    onClose();
  }

  const name = target?.clientName ?? "";
  return (
    <dialog ref={dialogRef} className="ht-window ht-confirm reminder-window call-window" aria-labelledby="call-window-title"
            onCancel={(e) => { e.preventDefault(); onClose(); }}>
      <div className="ht-window-frame ht-confirm-frame">
        <h2 className="ht-title ht-confirm-title" id="call-window-title">
          {target?.mode === "manage" ? `Call with ${name}` : `Book a call with ${name}`}
        </h2>

        <div className="reminder-when">
          <label className="reminder-field">
            <span className="reminder-label">Day</span>
            <input ref={dayRef} type="date" className="ht-input" value={draft.day} onChange={(e) => setDraft({ ...draft, day: e.target.value })} />
          </label>
          <label className="reminder-field">
            <span className="reminder-label">Time</span>
            <input type="time" className="ht-input" value={draft.time} onChange={(e) => setDraft({ ...draft, time: e.target.value })} />
          </label>
        </div>
        <fieldset className="reminder-field call-lengths">
          <legend className="reminder-label">Length</legend>
          <div className="call-length-row" role="radiogroup">
            {CALL_LENGTHS.map((m) => (
              <label key={m} className={draft.minutes === m ? "call-length is-on" : "call-length"}>
                <input type="radio" name="call-length" value={m} checked={draft.minutes === m}
                       onChange={() => setDraft({ ...draft, minutes: m })} />
                {m} min
              </label>
            ))}
          </div>
        </fieldset>
        <label className="reminder-field">
          <span className="reminder-label">Topic</span>
          <input className="ht-input" value={draft.topic} maxLength={CALL_TOPIC_MAX} placeholder="Optional"
                 onChange={(e) => setDraft({ ...draft, topic: e.target.value })}
                 onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void save(); } }} />
        </label>
        <p className="reminder-hint">In their Tasks. Both phones remind a day, an hour and at the start.</p>

        {refusal !== null && <p className="ht-callout reminder-refusal" role="alert">{refusal}</p>}

        {confirmCancel ? (
          <div className="ht-confirm-actions">
            <span className="reminder-confirm-words">Cancel this call?</span>
            <button type="button" className="ht-button" onClick={() => setConfirmCancel(false)}>Keep it</button>
            <button type="button" className="ht-button ht-button-danger" disabled={busy} onClick={() => { void cancelCall(); }}>Cancel call</button>
          </div>
        ) : (
          <div className="ht-confirm-actions">
            {target?.mode === "manage" && (
              <button type="button" className="ht-button ht-button-quiet reminder-delete" onClick={() => setConfirmCancel(true)}>Cancel call</button>
            )}
            <button type="button" className="ht-button" onClick={onClose}>Close</button>
            <button type="button" className="ht-button ht-button-primary" disabled={busy} onClick={() => { void save(); }}>
              {busy ? "Saving…" : target?.mode === "manage" ? "Save" : "Book"}
            </button>
          </div>
        )}
      </div>
    </dialog>
  );
}
